import { ErrorBoundary, LocationProvider, Route, Router, lazy } from 'preact-iso';
import { AppShell } from './ui/AppShell';
import { Spinner } from './ui/Spinner';

const HomePage = lazy(() => import('./pages/home/HomePage'));
const AboutPage = lazy(() => import('./pages/about/AboutPage'));
const LoginPage = lazy(() => import('./pages/login/LoginPage'));
const RegisterPage = lazy(() => import('./pages/register/RegisterPage'));
const StatusPage = lazy(() => import('./pages/status/StatusPage'));
const EvaluatorPage = lazy(() => import('./pages/evaluator/EvaluatorPage'));
const OfficePage = lazy(() => import('./pages/office/OfficePage'));
const OverviewPage = lazy(() => import('./pages/office/OverviewPage'));
const QueuePage = lazy(() => import('./pages/office/QueuePage'));
const CasesSearchPage = lazy(() => import('./pages/office/CasesSearchPage'));
const CasePage = lazy(() => import('./pages/office/CasePage'));
const ResponderPage = lazy(() => import('./pages/office/ResponderPage'));
const StaffPage = lazy(() => import('./pages/office/StaffPage'));
const SettingsPage = lazy(() => import('./pages/office/SettingsPage'));
const ExportsPage = lazy(() => import('./pages/office/ExportsPage'));
const IssuePage = lazy(() => import('./pages/issue/IssuePage'));
const VerifyPage = lazy(() => import('./pages/verify/VerifyPage'));
const StatsPage = lazy(() => import('./pages/stats/StatsPage'));
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const NotFoundPage = lazy(() => import('./pages/notfound/NotFoundPage'));

function RouteFallback() {
  return (
    <div class="container u-text-center" style={{ padding: '6rem 0' }}>
      <Spinner size={28} />
    </div>
  );
}

function RouteError({ error }: { error: Error }) {
  return (
    <div class="container u-text-center" style={{ padding: '6rem 0' }}>
      <h1>Something went wrong</h1>
      <p class="u-text-muted">{error.message}</p>
      <a class="c-btn c-btn--secondary" href="/">
        Back to Home
      </a>
    </div>
  );
}

/** The app root: routing + the page frame. Every route below WP5 owns is a "Coming in this build" placeholder — see src/pages/*. */
export function App() {
  return (
    <LocationProvider>
      <AppShell>
        <ErrorBoundary onError={console.error}>
          <Router>
            <Route path="/" component={HomePage} />
            <Route path="/about" component={AboutPage} />
            <Route path="/login" component={LoginPage} />
            <Route path="/register" component={RegisterPage} />
            {/* Param named "reference", not "ref" — preact-iso injects each path param as a same-named
                top-level prop, and "ref" collides with Preact's own reserved ref prop (crashes with
                "Component's ref property should be a function... but got [string]"). /office/case/:appRef
                avoids this the same way. */}
            <Route path="/status/:reference" component={StatusPage} />
            <Route path="/evaluator" component={EvaluatorPage} />
            {/* Explicit routes per office sub-page (not a nested <Router> inside OfficePage — preact-iso's
                Router doesn't reliably re-match a *second*, nested Router instance on a route() transition
                between two of its own Route entries; every office/evaluator page below owns its own
                OfficeLayout wrap instead, see src/pages/office/OfficeLayout.tsx). "/office/*" stays as a
                catch-all fallback for any other /office/... path. */}
            <Route path="/office" component={OverviewPage} />
            <Route path="/office/queue" component={QueuePage} />
            <Route path="/office/cases" component={CasesSearchPage} />
            {/* Named `:appRef`, not `:ref` — preact-iso's Router spreads route params onto the
                matched vnode's props, and a `ref` prop is intercepted by Preact itself (for
                DOM/component refs) rather than passed through, which silently breaks rendering
                on every client-side navigation into this route. */}
            <Route path="/office/case/:appRef" component={CasePage} />
            <Route path="/office/responder" component={ResponderPage} />
            <Route path="/office/staff" component={StaffPage} />
            <Route path="/office/settings" component={SettingsPage} />
            <Route path="/office/exports" component={ExportsPage} />
            <Route path="/office/*" component={OfficePage} />
            <Route path="/issue" component={IssuePage} />
            <Route path="/verify" component={VerifyPage} />
            <Route path="/stats" component={StatsPage} />
            <Route path="/dashboard" component={DashboardPage} />
            <Route default component={NotFoundPage} />
          </Router>
        </ErrorBoundary>
      </AppShell>
    </LocationProvider>
  );
}

// Re-exported so lazy-loaded routes can render a consistent fallback/error UI if needed elsewhere.
export { RouteFallback, RouteError };
